import { useCallback } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import TextField from "@mui/material/TextField";
import { lightScrollbarStyle } from "@/styles/lightScrollbarStyle";
import type { ExportFieldOption } from "./exportConfigurationFields";

interface Props {
  id: string;
  options: ExportFieldOption[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  groupBy?: (option: ExportFieldOption) => string;
}

export function FieldsAutocomplete({
  id,
  options,
  selectedValues,
  onChange,
  placeholder = "Search fields...",
  disabled = false,
  groupBy,
}: Props) {
  const value = options.filter((o) => selectedValues.includes(o.value));

  const handleChange = useCallback(
    (_: React.SyntheticEvent, newValue: ExportFieldOption[]) => {
      onChange(newValue.map((o) => o.value));
    },
    [onChange]
  );

  return (
    <Autocomplete
      id={id}
      multiple
      size="small"
      disableCloseOnSelect
      disabled={disabled}
      value={value}
      options={options}
      groupBy={groupBy}
      onChange={handleChange}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, val) => option.value === val.value}
      slotProps={{
        listbox: {
          id: `${id}-listbox`,
          sx: { maxHeight: 280, ...lightScrollbarStyle },
        },
      }}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          return (
            <Chip
              id={`${id}-chip-${option.value}`}
              key={key}
              label={option.label}
              size="small"
              data-testid={`${id}-chip-${option.value}`}
              {...tagProps}
            />
          );
        })
      }
      renderInput={(params) => (
        // The notched outline is a child of .MuiInputBase-root, so it scrolls
        // with the content when that root is made scrollable. Draw the border
        // on this stable outer Box instead, and hide MUI's own outline.
        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
          }}
        >
          <TextField
            {...params}
            placeholder={value.length === 0 ? placeholder : ""}
            variant="outlined"
            size="small"
            fullWidth
            inputProps={{ ...params.inputProps, "data-testid": `${id}-input` }}
            sx={{
              "& .MuiOutlinedInput-notchedOutline": { border: "none" },
              "& .MuiInputBase-root": {
                gap: 0.5,
                maxHeight: 280,
                overflowY: "auto",
                ...lightScrollbarStyle,
              },
            }}
          />
        </Box>
      )}
      renderOption={(props, option, { selected }) => {
        const { key, ...otherProps } = props;
        return (
          <li key={key} {...otherProps}>
            <Checkbox
              checked={selected}
              sx={{ marginRight: 1, padding: 0 }}
              disableRipple
              data-testid={`${id}-option-${option.value}-checkbox`}
            />
            {option.label}
          </li>
        );
      }}
    />
  );
}
